import { ShieldCheck, Leaf, FlaskConical, HeartPulse } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import ingredientHero from "@/assets/ingredient-hero-2.png";

const principles = [
  {
    icon: FlaskConical,
    title: "Evidence-based ingredients",
    description: "Every ingredient is chosen for a reason — backed by published research, not marketing trends.",
  },
  {
    icon: ShieldCheck,
    title: "No artificial fillers",
    description: "No artificial sweeteners, colours, or bulking agents. Nothing you wouldn't recognise.",
  },
  {
    icon: HeartPulse,
    title: "No exaggerated dosages",
    description: "Effective amounts, not extreme ones. Designed for daily use — safe, gentle, and consistent.",
  },
  {
    icon: Leaf,
    title: "EU-compliant & everyday safe",
    description: "Formulated within EU regulatory guidelines. Made for real life, not a lab experiment.",
  },
];

const IngredientPhilosophy = () => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-[hsl(var(--pastel-yellow))] py-24 md:py-32">
      <div
        ref={ref}
        className="mx-auto max-w-6xl px-6 flex flex-col lg:flex-row items-center gap-12 lg:gap-16"
      >
        {/* Left — Product image */}
        <div
          className={cn(
            "w-full lg:w-1/2 flex-shrink-0 transition-all duration-700 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <div className="rounded-3xl overflow-hidden">
            <img
              src={ingredientHero}
              alt="State Snacks products"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* Right — Copy + 2×2 grid */}
        <div className="w-full lg:w-1/2">
          <h2
            className={cn(
              "heading-bold text-3xl leading-[0.95] text-foreground sm:text-4xl lg:text-5xl transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            Functional. Honest.
            <br />
            Nothing to hide.
          </h2>
          <p
            className={cn(
              "mt-4 max-w-md text-base leading-relaxed text-foreground/60 sm:text-lg transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
            style={{ transitionDelay: "150ms" }}
          >
            We believe in calm, trustworthy formulation. Every ingredient earns its place.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-6">
            {principles.map((item, i) => (
              <div
                key={item.title}
                className={cn(
                  "flex flex-col items-center text-center gap-3 transition-all duration-700 ease-out",
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                )}
                style={{ transitionDelay: `${i * 100 + 250}ms` }}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-foreground/10">
                  <item.icon className="h-6 w-6 text-foreground" strokeWidth={1.5} />
                </div>
                <h3 className="font-extrabold text-sm text-foreground uppercase tracking-wide leading-tight">
                  {item.title}
                </h3>
                <p className="text-xs leading-relaxed text-foreground/55 hidden sm:block">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IngredientPhilosophy;
